import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BackLink } from '../components/BackLink';
import { ArrowLeft, FileText, Scale, Building2, CreditCard, Truck, Shield, Handshake, CheckCircle2, XCircle, Download, ChevronRight, Leaf, Package, FileSignature, Route } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { partners } from '../data/mock';
import { BrandLogos } from '../components/BrandLogos';
import { BreadcrumbPill } from '../components/Breadcrumb';
import { 
  cgvKdmarcheContent, 
  cgOscopContent, 
  conventionContent,
  charteESSContent,
  annexeLogiscopContent,
  contratTransportLogiscopContent,
  annexeTourneesESSContent,
  privacyContent,
  mentionsLegalesContent,
  auditComplianceTable,
  replaceVariables, 
  legalVariables,
  getDocumentById,
  allLegalDocuments
} from '../data/legalDocuments';

// Render markdown-like content
import { LegalDocument } from '../components/legal/LegalDocument';

export default function LegalPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Determine active tab based on URL parameter
  const getInitialTab = () => {
    if (docId === 'cg-oscop') return 'oscop';
    if (docId === 'convention') return 'convention';
    if (docId === 'charte-ess') return 'charte-ess';
    if (docId === 'annexe-logiscop') return 'logiscop';
    if (docId === 'contrat-transport') return 'transport';
    if (docId === 'annexe-ess-route' || docId === 'annexe-tournees-ess') return 'ess-route';
    if (docId === 'politique-confidentialite') return 'privacy';
    if (docId === 'mentions-legales') return 'mentions';
    return 'kdmarche';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getInitialTab());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);
  
  // Handle tab change
  const handleTabChange = (value) => {
    setActiveTab(value);
    const routes = {
      'kdmarche': '/legal/cgv-kdmarche',
      'oscop': '/legal/cg-oscop',
      'convention': '/legal/convention',
      'charte-ess': '/legal/charte-ess',
      'logiscop': '/legal/annexe-logiscop',
      'transport': '/legal/contrat-transport',
      'ess-route': '/legal/annexe-ess-route',
      'privacy': '/legal/politique-confidentialite',
      'mentions': '/legal/mentions-legales'
    };
    navigate(routes[value] || '/legal/cgv-kdmarche');
  };
  
  return (
    <div 
      className="min-h-screen text-white"
      style={{ background: 'linear-gradient(180deg, #2A1045 0%, #451F6B 55%, #2A1045 100%)' }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-50"
        style={{
          background: 'rgba(30,12,52,0.94)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(212,175,55,0.32)'
        }}
      >
        <div className="max-w-[1000px] mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackLink fallback="/" data-testid="legal-back-btn" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('legalPage.back')}</span>
            </BackLink>
            <div className="flex items-center gap-2">
              <BrandLogos size="sm" />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-[#D9B35A]" />
            <span className="text-sm text-white/70">{t('legalPage.docs_badge')}</span>
          </div>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-5 py-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <BreadcrumbPill />
        </div>

        {/* Page Title */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold mb-2">{t('legalPage.title')}</h1>
          <p className="text-white/60">
            {t('legalPage.subtitle')}
          </p>
        </div>

        {!(i18n.language || 'fr').startsWith('fr') && (
          <div className="mb-6 p-3 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/25 text-center" data-testid="legal-fr-authentic-banner">
            <p className="text-sm text-[#F5C97B]">{t('legalPage.fr_authentic')}</p>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="flex flex-wrap justify-center w-full max-w-5xl mx-auto bg-white/[0.04] border border-white/[0.08] p-1 h-auto">
            <TabsTrigger
              value="mentions"
              className="data-[state=active]:bg-[#D9B35A]/20 data-[state=active]:text-[#D9B35A] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-mentions-legales"
            >
              <Scale className="w-4 h-4" />
              <span className="hidden lg:inline">{t('legalPage.tab_mentions')}</span><span className="lg:hidden">{t('legalPage.tab_mentions_short')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="kdmarche"
              className="data-[state=active]:bg-[#D9B35A]/20 data-[state=active]:text-[#D9B35A] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-cgv-kdmarche"
            >
              <Truck className="w-4 h-4" />
              <span className="hidden lg:inline">{t('legalPage.tab_cgv')}</span> KDM
            </TabsTrigger>
            <TabsTrigger 
              value="oscop"
              className="data-[state=active]:bg-[#D4AF37]/20 data-[state=active]:text-[#D4AF37] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-cg-oscop"
            >
              <CreditCard className="w-4 h-4" />
              <span className="hidden lg:inline">{t('legalPage.tab_cgu')}</span> CommunityPlace
            </TabsTrigger>
            <TabsTrigger 
              value="convention"
              className="data-[state=active]:bg-[#8B5CF6]/20 data-[state=active]:text-[#8B5CF6] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-convention"
            >
              <Handshake className="w-4 h-4" />
              <span className="hidden lg:inline">{t('legalPage.tab_convention')}</span>
              <span className="lg:hidden">{t('legalPage.tab_convention_short')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="charte-ess"
              className="data-[state=active]:bg-[#10B981]/20 data-[state=active]:text-[#10B981] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-charte-ess"
            >
              <Leaf className="w-4 h-4" />
              <span className="hidden lg:inline">{t('legalPage.tab_charte')}</span> ESS
            </TabsTrigger>
            <TabsTrigger 
              value="logiscop"
              className="data-[state=active]:bg-[#8B5CF6]/20 data-[state=active]:text-[#8B5CF6] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-logiscop"
            >
              <Package className="w-4 h-4" />
              <span className="hidden lg:inline">LOGI&apos;</span>SCOP
            </TabsTrigger>
            <TabsTrigger 
              value="transport"
              className="data-[state=active]:bg-[#F59E0B]/20 data-[state=active]:text-[#F59E0B] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-contrat-transport"
            >
              <FileSignature className="w-4 h-4" />
              <span>{t('legalPage.tab_transport')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="ess-route"
              className="data-[state=active]:bg-[#10B981]/20 data-[state=active]:text-[#10B981] flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-ess-route"
            >
              <Route className="w-4 h-4" />
              <span className="hidden lg:inline">{t('legalPage.tab_tournees')}</span> ESS
            </TabsTrigger>
            <TabsTrigger 
              value="privacy"
              className="data-[state=active]:bg-[#0B4D87]/30 data-[state=active]:text-sky-300 flex items-center gap-1 py-2.5 text-xs sm:text-sm"
              data-testid="tab-privacy"
            >
              <Shield className="w-4 h-4" />
              <span className="hidden lg:inline">{t('legalPage.tab_privacy')}</span><span className="lg:hidden">{t('legalPage.tab_privacy_short')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kdmarche" data-testid="content-cgv-kdmarche">
            <LegalDocument document={cgvKdmarcheContent} />
          </TabsContent>

          <TabsContent value="oscop" data-testid="content-cg-oscop">
            <LegalDocument document={cgOscopContent} />
          </TabsContent>

          <TabsContent value="convention" data-testid="content-convention">
            <LegalDocument document={conventionContent} />
          </TabsContent>
          
          <TabsContent value="charte-ess" data-testid="content-charte-ess">
            <LegalDocument document={charteESSContent} />
          </TabsContent>

          <TabsContent value="privacy" data-testid="content-privacy">
            <LegalDocument document={privacyContent} />
          </TabsContent>

          <TabsContent value="mentions" data-testid="content-mentions-legales">
            <LegalDocument document={mentionsLegalesContent} />
          </TabsContent>

          <TabsContent value="logiscop" data-testid="content-logiscop">
            <LegalDocument document={annexeLogiscopContent} />
            {/* Tarification LOGI'SCOP */}
            {annexeLogiscopContent.tarification && (
              <div className="mt-8 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08]">
                <h3 className="text-lg font-semibold text-white/90 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#8B5CF6]" />
                  {annexeLogiscopContent.tarification.title}
                </h3>
                <p className="text-sm text-white/60 mb-4">{annexeLogiscopContent.tarification.description}</p>
                
                {/* Zones Rates Table */}
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 px-3 text-white/60 font-medium">{t('legalPage.zone')}</th>
                        <th className="text-center py-2 px-3 text-white/60 font-medium">{t('legalPage.base')}</th>
                        <th className="text-center py-2 px-3 text-white/60 font-medium">€/kg</th>
                        <th className="text-center py-2 px-3 text-white/60 font-medium">€/m³</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annexeLogiscopContent.tarification.zones.map((zone) => (
                        <tr key={`logi-zone-${zone.code}`} className="border-b border-white/5">
                          <td className="py-2.5 px-3">
                            <span className="text-white/80">{zone.name}</span>
                            <span className="ml-2 text-xs text-white/40">({zone.code})</span>
                          </td>
                          <td className="py-2.5 px-3 text-center text-[#8B5CF6] font-medium">{zone.base}</td>
                          <td className="py-2.5 px-3 text-center text-white/70">{zone.perKg}</td>
                          <td className="py-2.5 px-3 text-center text-white/70">{zone.perM3}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Supplements */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/15">
                    <h4 className="text-sm font-semibold text-[#8B5CF6] mb-3">{t('legalPage.supplements_title')}</h4>
                    <div className="space-y-2">
                      {annexeLogiscopContent.tarification.supplements.map((sup) => (
                        <div key={`supp-${sup.label}`} className="flex justify-between text-sm">
                          <span className="text-white/70">{sup.label}</span>
                          <span className="text-white/90 font-medium">{sup.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.08]">
                    <h4 className="text-sm font-semibold text-white/80 mb-3">{t('legalPage.prep_title')}</h4>
                    <div className="space-y-2">
                      {annexeLogiscopContent.tarification.preparation.map((prep) => (
                        <div key={`prep-${prep.label}`} className="flex justify-between text-sm">
                          <span className="text-white/60">{prep.label}</span>
                          <span className="text-white/80">{prep.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-xs text-white/50 italic">{annexeLogiscopContent.tarification.tvaNote}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="transport" data-testid="content-contrat-transport">
            <LegalDocument document={contratTransportLogiscopContent} />
            
            {/* Disclaimer box */}
            <div className="mt-6 p-5 rounded-2xl bg-[#F59E0B]/8 border border-[#F59E0B]/20">
              <p className="text-xs uppercase tracking-wider text-[#F59E0B] font-semibold mb-3 flex items-center gap-2">
                <FileSignature className="w-4 h-4" />
                {t('legalPage.checkout_clause')}
              </p>
              <p className="text-base text-white/90 italic leading-relaxed font-medium">
                {contratTransportLogiscopContent.disclaimer}
              </p>
            </div>

            {/* Parties info */}
            {contratTransportLogiscopContent.parties && (
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/15">
                  <h4 className="text-sm font-semibold text-[#8B5CF6] mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    {t('legalPage.carrier')}
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-white/90 font-medium">{contratTransportLogiscopContent.parties.transporteur.name}</p>
                    <p className="text-white/60">{contratTransportLogiscopContent.parties.transporteur.form}</p>
                    <p className="text-white/50 text-xs">{contratTransportLogiscopContent.parties.transporteur.role}</p>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/15">
                  <h4 className="text-sm font-semibold text-[#F59E0B] mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {t('legalPage.client_b2b')}
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-white/90 font-medium">{replaceVariables(contratTransportLogiscopContent.parties.client.name)}</p>
                    <p className="text-white/60">{contratTransportLogiscopContent.parties.client.role}</p>
                    <p className="text-white/50 text-xs">{t('legalPage.identified_order')}</p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ess-route" data-testid="content-ess-route">
            <LegalDocument document={annexeTourneesESSContent} />
            
            {/* Official clause */}
            <div className="mt-6 p-5 rounded-2xl bg-[#10B981]/8 border border-[#10B981]/20">
              <p className="text-xs uppercase tracking-wider text-[#10B981] font-semibold mb-3 flex items-center gap-2">
                <Route className="w-4 h-4" />
                {t('legalPage.checkout_clause_ess')}
              </p>
              <p className="text-base text-white/90 italic leading-relaxed font-medium">
                {annexeTourneesESSContent.officialClause}
              </p>
            </div>

            {/* Tarification ESS Route */}
            {annexeTourneesESSContent.tarification && (
              <div className="mt-8 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08]">
                <h3 className="text-lg font-semibold text-white/90 mb-4 flex items-center gap-2">
                  <Route className="w-5 h-5 text-[#10B981]" />
                  {annexeTourneesESSContent.tarification.title}
                </h3>
                <p className="text-sm text-white/60 mb-4">{annexeTourneesESSContent.tarification.description}</p>
                
                {/* ESS Benefits */}
                <div className="grid md:grid-cols-3 gap-3 mb-6">
                  {annexeTourneesESSContent.tarification.benefits.map((benefit) => (
                    <div key={`ess-benefit-${benefit.label}`} className="p-3 rounded-xl bg-[#10B981]/5 border border-[#10B981]/15 text-center">
                      <p className="text-xs text-[#10B981] uppercase tracking-wider mb-1">{benefit.label}</p>
                      <p className="text-white/90 font-semibold">{benefit.value}</p>
                    </div>
                  ))}
                </div>
                
                {/* Zones Rates Table */}
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 px-3 text-white/60 font-medium">{t('legalPage.zone')}</th>
                        <th className="text-center py-2 px-3 text-white/60 font-medium">{t('legalPage.base')}</th>
                        <th className="text-center py-2 px-3 text-white/60 font-medium">€/kg</th>
                        <th className="text-center py-2 px-3 text-white/60 font-medium">€/carton</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annexeTourneesESSContent.tarification.zones.map((zone) => (
                        <tr key={`ess-zone-${zone.code}`} className="border-b border-white/5">
                          <td className="py-2.5 px-3">
                            <span className="text-white/80">{zone.name}</span>
                            <span className="ml-2 text-xs text-white/40">({zone.code})</span>
                          </td>
                          <td className="py-2.5 px-3 text-center text-[#10B981] font-medium">{zone.base}</td>
                          <td className="py-2.5 px-3 text-center text-white/70">{zone.perKg}</td>
                          <td className="py-2.5 px-3 text-center text-white/70">{zone.perCarton}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-4 text-xs text-white/50 italic">{annexeTourneesESSContent.tarification.tvaNote}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer Note */}
        <div className="mt-12 p-4 rounded-xl bg-white/[0.02] border border-white/[0.08] text-center">
          <p className="text-xs text-white/50">
            {t('legalPage.footer_note')}{' '}
            <a href="mailto:juridique@objectifscopoutremer.com" className="text-[#D9B35A] hover:underline">
              juridique@objectifscopoutremer.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
